/**
 * CC-1c — per-viewport edit shell-audit emitter (extracted from
 * geometryShellReducer.ts to keep it under the CLAUDE.md 2000-line limit).
 *
 * The per-viewport override commands (VPLAYER / VPCLIP / lock / shade-plot /
 * scale / view) mutate the geometry document via `applyLayoutAction` but
 * previously hit `geometryShellReducer`'s `default → null` and emitted NO
 * audit event. This module owns their typed shell-audit emission so ТЗ §14
 * command tracking covers each op (and the CC-1a transport gets a typed event
 * to carry — `describeAuditOperation` attaches the viewportId + op-specific
 * params automatically via `pushAuditEvent`).
 *
 * Returns the next shell state when `command` is one of the viewport-edit
 * commands, or `null` so the caller's `default` can fall through unchanged.
 */

import type {
  DynamicBlockCommand,
  EditorCommand,
  EditorShellState,
} from "@dxf-ai-editor/contracts";
import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";

import { t } from "../i18n";
import { pushAuditEvent } from "./reducerHelpers";
import { computeViewportEditMutation } from "./viewportEditMutation";
import { shadePlotModeLabel } from "./viewportShadePlot";

export function reduceViewportEditShellAudit(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  geometryDocument: DrawingDocument,
): EditorShellState | null {
  // FIX #269 — gate the success audit on a GENUINE mutation. The engine's
  // `applyViewport*` helpers report `mutated:false` for a no-op: a stale /
  // nonexistent viewport id, the paper-space control viewport (id === 1, never
  // user-modifiable), an idempotent VPLAYER, or an unchanged set-view. Such a
  // no-op must emit NO false success audit / telemetry / transcript row (mirrors
  // the App.tsx zoom/pan mutated-gate — single source of truth in
  // `computeViewportEditMutation`).
  const mutationResult = computeViewportEditMutation(geometryDocument, command);
  // Not one of the viewport-edit commands — let the caller's default fall
  // through unchanged.
  if (mutationResult === null) return null;
  // Matched a viewport-edit command but it was a no-op — handled here (return
  // the unchanged state) so the caller does NOT fall through, but emit no audit.
  if (!mutationResult.mutated) return state;

  // @codex #496 P2 — the engine matches the viewport id CASE-INSENSITIVELY
  // (`VPCLIP 2a …` → stored `2A`), so `command.viewportId` can be the raw typed
  // token, not the stored entity id. Canonicalize it to the MATCHED entity's id
  // (`mutationResult.canonicalViewportId`) BEFORE emitting so the audit summary,
  // the typed `params.viewportId` (via `describeAuditOperation`), AND the persisted
  // affected-entity ids (via `affectedEntityIdsForCommand`) all point at the real
  // stored viewport, not the token. `mutationResult !== null && mutated` already
  // guarantees `command` is one of the seven viewport-edit commands — each carries
  // `viewportId` — so re-spreading it back to `typeof command` is sound.
  const canonicalViewportId = mutationResult.canonicalViewportId;
  const auditCommand =
    canonicalViewportId !== undefined &&
    canonicalViewportId !== (command as { viewportId?: string }).viewportId
      ? ({ ...command, viewportId: canonicalViewportId } as typeof command)
      : command;

  switch (auditCommand.type) {
    case "geometry/viewport-set-lock":
      return pushAuditEvent(
        state,
        auditCommand,
        auditCommand.locked
          ? t("audit.viewport_locked", { id: auditCommand.viewportId })
          : t("audit.viewport_unlocked", { id: auditCommand.viewportId }),
        "success",
      );
    case "geometry/viewport-set-shadeplot":
      return pushAuditEvent(
        state,
        auditCommand,
        t("audit.viewport_shadeplot_set", {
          id: auditCommand.viewportId,
          // `shadePlotMode` is optional: an undo inverse restoring an absent
          // group-170 record carries `undefined`. Label it as the group-170
          // default ("As Displayed" = 0) for the audit summary only — this does
          // not affect the persisted field (the mutation clears it).
          mode: shadePlotModeLabel(auditCommand.shadePlotMode ?? 0, t),
        }),
        "success",
      );
    case "geometry/viewport-vplayer":
      return pushAuditEvent(
        state,
        auditCommand,
        auditCommand.action === "freeze"
          ? t("audit.viewport_vplayer_freeze", {
              layer: auditCommand.layerName,
              id: auditCommand.viewportId,
            })
          : t("audit.viewport_vplayer_thaw", {
              layer: auditCommand.layerName,
              id: auditCommand.viewportId,
            }),
        "success",
      );
    case "geometry/viewport-clip-set":
      return pushAuditEvent(
        state,
        auditCommand,
        t("audit.viewport_clip_set", { id: auditCommand.viewportId }),
        "success",
      );
    case "geometry/viewport-clip-set-polygon":
      return pushAuditEvent(
        state,
        auditCommand,
        t("audit.viewport_clip_polygon_set", { id: auditCommand.viewportId }),
        "success",
      );
    case "geometry/viewport-clip-remove":
      return pushAuditEvent(
        state,
        auditCommand,
        t("audit.viewport_clip_removed", { id: auditCommand.viewportId }),
        "success",
      );
    case "geometry/viewport-set-scale":
      return pushAuditEvent(
        state,
        auditCommand,
        t("audit.viewport_scale_set", {
          id: auditCommand.viewportId,
          num: auditCommand.scaleNum,
          denom: auditCommand.scaleDenom,
        }),
        "success",
      );
    case "geometry/viewport-set-view":
      return pushAuditEvent(
        state,
        auditCommand,
        t("audit.viewport_view_updated", { id: auditCommand.viewportId }),
        "success",
      );
    default:
      return null;
  }
}
